import React from 'react';
import moment from 'moment';
import classnames from 'classnames';
import * as R from 'ramda/src/index';
import {formatMonthDayPeriod} from 'lib/helpers/globalize-helper';
import {
  getPortCode, accomodationUrl, startsWithLowercase, lowerCaseAllWordsExceptFirstLetter, isGreekLocale
} from 'ferries/helpers/common';
import {cms} from 'helpers/stableValues';
import bookingPorts from 'ferries/Confirmation/Widgets/bookingPorts.json';
import {isGreekText} from 'providers/ferries/autocompleteResultsBasedOnTypedLanguage/utils';

const ONEWAY_TRIP_TYPE = 0;
const ROUND_TRIP_TYPE = 1;
const MULTI_TRIP_TYPE = 2;

const carrierCode = cms?.abbr?.slice(0, 3);

const themePort = {
  container: 'dropdown',
  containerOpen: 'open',
  input: 'form-control form-control-lg rounded-0 pr-6',
  inputOpen: 'react-autosuggest__input--open',
  inputFocused: 'react-autosuggest__input--focused',
  suggestionsContainer: 'dropdown-menu w-1-1 py-0',
  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
  suggestionsList: 'list-group list-group-flush scrollable-menu suggestion-list',
  suggestion: 'ferry-port-suggestion',
  suggestionFirst: 'react-autosuggest__suggestion--first',
  suggestionHighlighted: 'bg-light',
  sectionContainer: 'react-autosuggest__section-container',
  sectionContainerFirst: 'react-autosuggest__section-container--first',
  sectionTitle: 'react-autosuggest__section-title'
};

const themePortModal = {
  container: '',
  containerOpen: 'open',
  input: 'form-control form-control-lg rounded pr-6',
  inputOpen: 'react-autosuggest__input--open',
  inputFocused: 'react-autosuggest__input--focused',
  suggestionsContainer: 'w-1-1 py-0 border-0', // dropdown-menu removed due to the list not be displayed
  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open hidden', // hidden
  suggestionsList: 'list-group list-group-flush scrollable-menu suggestion-list',
  suggestion: 'ferry-port-suggestion',
  suggestionFirst: 'react-autosuggest__suggestion--first',
  suggestionHighlighted: 'bg-light',
  sectionContainer: 'react-autosuggest__section-container',
  sectionContainerFirst: 'react-autosuggest__section-container--first',
  sectionTitle: 'react-autosuggest__section-title'
};

const selectAll = (event) => {
  event.target.focus();
  event.target.setSelectionRange(0, event.target.value.length);
};

const transformQuery = ({trips, typeTrip, passengers, vehicles}) => {
  const nofTrips = trips.length;
  const {dep, arr, obDate, ibDate} = trips.reduce((acc, {departure, arrival, obDate, ibDate}, tripIndex) => {
    const isLastTrip = tripIndex === nofTrips - 1;
    acc.dep += `${getPortCode(departure)}${isLastTrip ? '' : ','}`;
    acc.arr += `${getPortCode(arrival)}${isLastTrip ? '' : ','}`;
    acc.obDate += `${obDate}${isLastTrip ? '' : ','}`;
    acc.ibDate += `${ibDate}${isLastTrip ? '' : ','}`;

    return acc;
  }, {dep: '', arr: '', obDate: '', ibDate: ''});

  return {
    dep,
    arr,
    obDate,
    ibDate,
    typeTrip,
    passengers,
    vehicles
  };
};

const getInvertType = (type) => type === 'dep' ? 'arr' : 'dep';

const justThePortName = (port) => port.split('(')[0].split(',')[0].trim();

const findByCode = (originalPorts, code) => originalPorts.find((port) => port.locationAbbr === code);

const getAccomodationUrl = (typeTrip, {arr, obDate, ibDate}, passengers) => {
  const {type, value} = bookingPorts[getPortCode(arr)] ?? bookingPorts['ATH'];
  return accomodationUrl(393113, obDate, ibDate, typeTrip === ROUND_TRIP_TYPE, type, value, passengers);
};

const metropolitanAndChildren = (acc, selectedPort, ports, pattern) => {
  acc.push({
    ...selectedPort,
    isMatch: startsWithLowercase(selectedPort.locationName, pattern),
    hasParrent: false,
    isLast: false
  });
  const portsWithParrent = ports.filter((port) =>
    selectedPort.childPorts.includes(port.ferryOwnLocationID) && port.dependentPorts.length > 0
  ).sort((a, b) => startsWithLowercase(b.locationName, pattern) - startsWithLowercase(a.locationName, pattern));

  const lastIndexPort = portsWithParrent.length - 1;
  portsWithParrent.forEach((port, index) => {

    const found = acc.find(({
      ferryOwnLocationID,
      locationName
    }) => ferryOwnLocationID === port.ferryOwnLocationID && locationName === port.locationName);
    // Deduplicate metropolitan ports
    if (!found) {
      acc.push({
        ...port,
        hasParrent: true,
        isMatch: startsWithLowercase(port.locationName, pattern),
        isLast: index === lastIndexPort
      });
    }
  });
};

const hierarchyPorts = ({selectedPorts, matchPorts, pattern}) => selectedPorts.reduce((acc, selectedPort) => {
  // Add the port to the list if it's not already there or if it exists in multiple languages
  const foundPort = acc.find(({
    ferryOwnLocationID,
    locationName
  }) => (ferryOwnLocationID === selectedPort.ferryOwnLocationID && locationName === selectedPort.locationName));

  if (!foundPort) {
    if (selectedPort.isMetropolitan) {
      metropolitanAndChildren(acc, selectedPort, matchPorts, pattern);
    } else {
      const metropolitanPort = matchPorts.find(({ferryOwnLocationID}) =>
        ferryOwnLocationID === selectedPort.parentLocationID);

      const parentFoundInSelPorts = selectedPorts.find(({ferryOwnLocationID}) =>
        ferryOwnLocationID === metropolitanPort?.ferryOwnLocationID);

      if (!metropolitanPort || (metropolitanPort && !parentFoundInSelPorts)) {
        acc.push({
          ...selectedPort,
          isMatch: startsWithLowercase(selectedPort.locationName, pattern),
          hasParrent: false,
          isLast: false
        });
      }
    }
  }
  return acc;
}, []);

const renderPortSuggestion = ({locationName, locationCountry, isMetropolitan, hasParrent, isMatch, isLast}) => {
  const portName = lowerCaseAllWordsExceptFirstLetter(locationName);
  const portClasses = classnames('d-block position-relative px-3', {
    'font-bold': isMetropolitan || isMatch,
    'pl-5': hasParrent,
    'border-bottom': isLast
  });

  return (
    <span className={portClasses} style={{padding: '12px'}}>
      {!isMetropolitan && <i className="icon icon-anchor mr-2" />}
      {`${portName}, ${locationCountry}`}
    </span>
  );
};

const renderRecentSearches = ({trips, typeTrip}, textTrip) => {
  const lastIndex = trips.length - 1;
  const lastTrip = trips[lastIndex];

  const departure = justThePortName(trips[0].departure);
  const arrival = justThePortName(lastTrip.arrival);
  const depDate = trips[0].obDate;
  const arrDate = lastTrip.obDate;
  const isRoundTrip = typeTrip === ROUND_TRIP_TYPE && departure === arrival;

  return (
    <>
      <span>
        {
          `${departure} - ${(isRoundTrip ? justThePortName(trips[0].arrival) : arrival)}`
        }
        {typeTrip === MULTI_TRIP_TYPE && ` (${trips.length} ${textTrip})`}
      </span><br />
      <span className="text-muted">
        {formatMonthDayPeriod(moment(depDate))}
        {
          [ROUND_TRIP_TYPE, MULTI_TRIP_TYPE].includes(typeTrip) &&
          ` - ${formatMonthDayPeriod(moment(isRoundTrip ? lastTrip.ibDate : arrDate))}`
        }
      </span>

    </>
  );
};

const getPopularPorts = (number, isDepartureGreek = undefined) => {
  if (isDepartureGreek === undefined) {
    return R.pipe(
      R.filter(({filtered, locationName}) => filtered &&
        (isGreekLocale ? isGreekText(locationName) : !isGreekText(locationName))),
      R.take(number)
    );
  }

  return R.pipe(
    R.filter(({filtered, locationName}) => filtered &&
      (isDepartureGreek ? isGreekText(locationName) : !isGreekText(locationName))),
    R.take(number)
  );

};

const getPopularPortsCountries = (ports) => {
  const portsLocale = ports.filter(({countryName}) =>
    isGreekLocale ? isGreekText(countryName) : !isGreekText(countryName));
  const uniqPorts = portsLocale.reduce((acc, {locationCountry, countryName}) => {
    const country = acc.find(({code}) =>
      code === locationCountry && (isGreekLocale ? isGreekText(countryName) : !isGreekText(countryName)));
    if (!country) {
      acc.push({
        code: locationCountry,
        name: countryName,
        selected: false
      });
    }

    return acc;
  }, []);

  return uniqPorts.sort((a, b) => a.name.localeCompare(b.name));
};

const getMatchPorts = ({suggestion, ports, isGreek}) => {
  const dependentPorts = (suggestion?.dependentPorts ?? [])
    .filter((port) => ![suggestion.parentLocationID, suggestion.ferryOwnLocationID].includes(port));

  // WE FILTER THE POSSIBLE PORTS BASED ON DEPENDENT PORTS
  const matchPorts = dependentPorts.reduce((acc, dependentPort) => {
    const foundPorts = ports.filter(({
      ferryOwnLocationID,
      plaintext
    }) => ferryOwnLocationID === dependentPort && isGreekText(plaintext) === isGreek);

    if (foundPorts.length) {
      acc.push(...foundPorts);
    }
    return acc;
  }, []);

  return matchPorts;
};

export {
  ONEWAY_TRIP_TYPE,
  ROUND_TRIP_TYPE,
  MULTI_TRIP_TYPE,
  carrierCode,
  themePort,
  themePortModal,
  selectAll,
  transformQuery,
  getInvertType,
  justThePortName,
  findByCode,
  getAccomodationUrl,
  metropolitanAndChildren,
  hierarchyPorts,
  renderPortSuggestion,
  renderRecentSearches,
  getPopularPorts,
  getMatchPorts,
  getPopularPortsCountries
};
